import React from 'react';
import {
  Document,
  Text,
} from '@react-pdf/renderer';

import _ from 'lodash';

import { PHOTO_SOURCES, THEME_PHOTO_REQUIREMENTS } from '../../../globals';

import formatPdfFileName from '../../_libs/utils/formatPdfFileName';

import { TopFeatureType, FeatureLogicDataType, ThemeDataType } from '../../_libs/data/types';

import BackCover from './pages/BackCover';
import ClinicalGuide from './pages/ClinicalGuide';
import Cover from './pages/Cover';
import Cta from './pages/Cta';
import DataPage from './pages/Data';
import Flyer from './pages/Flyer';
import GuideCover from './pages/GuideCover';
import Instagram from './pages/Instagram';
import LinkedIn from './pages/LinkedIn';
import MaterialsCover from './pages/MaterialsCover';
import TableOfContents from './pages/TableOfContents';
import ThemeCover from './pages/ThemeCover';

const DEBUG = false;

/**
 * Returns a random image that matches the theme requirments and the race
 * or ethnicity passed into the method call
 * @param relevantThemeData 
 * @param raceEthnicity 
 * @returns {string} - url of image for <img src={imgSrc} />
 */
function getPhotoForThemeAndRaceEthnicity (relevantThemeData, raceEthnicity) {
  let sex = (Math.random() > 0.5) ? 'female' : 'male';
  let age = (Math.random() > 0.5) ? 'olderAdult' : 'adult';
  let imgSrc;
  
  const requiredThemeImages = _.keys(THEME_PHOTO_REQUIREMENTS);
  if (_.indexOf(requiredThemeImages, `${relevantThemeData.id}`) > -1) {
    const themeConstraints = THEME_PHOTO_REQUIREMENTS[relevantThemeData.id];
    if (themeConstraints.age === 'child') {
      imgSrc = PHOTO_SOURCES[raceEthnicity].child;
    } else {
      if (themeConstraints.sex) {
        sex = themeConstraints.sex;
      }
      if (themeConstraints.age){
        age = themeConstraints.age;
      }
      imgSrc = PHOTO_SOURCES[raceEthnicity][age][sex];
    }
  } else {
    imgSrc = PHOTO_SOURCES[raceEthnicity][age][sex];
  }

  return imgSrc;
}

const RadianPDF = ({
  isCoBranded,
  coBrand,
  featureLogicData,
  lbBrand,
  primaryBrand,
  radius,
  reportData,
  themeData,
  topRaceEthnicities,
  topThemes,
  uuid,
  zipCode,
  setPdfBlob,
  pdfBlob,
}): JSX.Element => {

  // Do not render element if...
  if (
    // isCoBranded, but no coBrand data
    (isCoBranded && !coBrand) || 
    (
      !featureLogicData || 
      !lbBrand ||
      !primaryBrand || 
      !radius ||
      !reportData || 
      !themeData || 
      !topRaceEthnicities || 
      !topThemes || 
      !uuid || 
      !zipCode
    )
  ) {
    return null;
  }

  // This function calls functions to generate many sibling components
  let currentKey = 1;

  function generateFlyers (topThemes, themeData, topRaceEthnicities) {
    const pages = [];
    pages.push(
      <MaterialsCover
        key={currentKey}
        primaryBrand={primaryBrand}
        sectionTypeText="Community-Facing Materials"
        sectionTitleText="Printable Flyers"
        bookmark="Community-Facing Materials: Printable Flyers"
        uuid={uuid}
      >
        <Text>These flyers are formatted to fit Letter paper, one of the most common sizes in the United States. The flyers may be handed out on premises or posted in the community.</Text>
      </MaterialsCover>
    );
    currentKey++;

    _.map(topThemes, (topTheme) => {
      const relevantThemeData = _.find(themeData, { id: topTheme.themeId });
      const themeCoverProps = {
        key: currentKey,
        primaryBrand,
        themeData: relevantThemeData,
        topRaceEthnicities,
        radius,
        zipCode,
        uuid,
      };
      pages.push( <ThemeCover {...themeCoverProps} /> );
      currentKey++;

      _.map(topRaceEthnicities, (raceEthnicity, i) => {
        if (DEBUG && parseInt(i) > 0) { return; }
  
        const photoImgSrc = getPhotoForThemeAndRaceEthnicity(relevantThemeData, raceEthnicity);
        pages.push(
          <Flyer
            key={currentKey}
            isCoBranded={isCoBranded}
            coBrand={coBrand}
            lbBrand={lbBrand}
            // reportData={reportData}
            themeData={relevantThemeData}
            imgSrc={photoImgSrc}
            primaryBrand={primaryBrand}
            // raceEthnicity={raceEthnicity}
            radius={radius}
            zipCode={zipCode}
            uuid={uuid}
          />
        );
        currentKey++;
      });
    });
    return pages;
  }

  function generateLinkedIn (topThemes, themeData, topRaceEthnicities) {
    const pages = [];
    pages.push(
      <MaterialsCover
        key={currentKey}
        primaryBrand={primaryBrand}
        sectionTypeText="Community-Facing Materials"
        sectionTitleText="Shareable Social Graphics, Rectangular"
        bookmark="Community-Facing Materials: Socials – Rectangular"
        uuid={uuid}
      >
        <Text>These graphics are best shared on LinkedIn, Twitter, or Facebook.</Text>
      </MaterialsCover>
    );
    currentKey++;

    _.map(topThemes, (topTheme) => {
      const relevantThemeData = _.find(themeData, { id: topTheme.themeId });
      const themeCoverProps = {
        key: currentKey,
        primaryBrand,
        themeData: relevantThemeData,
        topRaceEthnicities,
        radius,
        zipCode,
        uuid,
      };
      pages.push( <ThemeCover {...themeCoverProps} /> );
      currentKey++;

      _.map(topRaceEthnicities, (raceEthnicity, i) => {
        if (DEBUG && parseInt(i) > 0) { return; }
  
        const photoImgSrc = getPhotoForThemeAndRaceEthnicity(relevantThemeData, raceEthnicity);
        pages.push(
          <LinkedIn
            key={currentKey}
            isCoBranded={isCoBranded}
            coBrand={coBrand}
            lbBrand={lbBrand}
            reportData={reportData}
            themeData={relevantThemeData}
            imgSrc={photoImgSrc}
            primaryBrand={primaryBrand}
            raceEthnicity={raceEthnicity}
            radius={radius}
            zipCode={zipCode}
            uuid={uuid}
          />
        );
        currentKey++;
      });
    });
    return pages;
  }

  function generateInstagram (topThemes, themeData, topRaceEthnicities) {
    const pages = [];
    pages.push(
      <MaterialsCover
        key={currentKey}
        primaryBrand={primaryBrand}
        sectionTypeText="Community-Facing Materials"
        sectionTitleText="Shareable Social Graphics, Square"
        bookmark="Community-Facing Materials: Socials – Square"
        uuid={uuid}
      >
        <Text>These graphics are best shared on Instagram or Facebook.</Text>
      </MaterialsCover>
    );
    currentKey++;

    _.map(topThemes, (topTheme) => {
      const relevantThemeData = _.find(themeData, { id: topTheme.themeId });
      const themeCoverProps = {
        key: currentKey,
        primaryBrand,
        themeData: relevantThemeData,
        topRaceEthnicities,
        radius,
        zipCode,
        uuid,
      };
      pages.push( <ThemeCover {...themeCoverProps} /> );
      currentKey++;

      _.map(topRaceEthnicities, (raceEthnicity, i) => {
        if (DEBUG && parseInt(i) > 0) { return; }
  
        const photoImgSrc = getPhotoForThemeAndRaceEthnicity(relevantThemeData, raceEthnicity);
        pages.push(
          <Instagram
            key={currentKey}
            isCoBranded={isCoBranded}
            coBrand={coBrand}
            lbBrand={lbBrand}
            // reportData={reportData}
            themeData={relevantThemeData}
            imgSrc={photoImgSrc}
            primaryBrand={primaryBrand}
            // raceEthnicity={raceEthnicity}
            radius={radius}
            zipCode={zipCode}
            uuid={uuid}
          />
        );
        currentKey++;
      });
    });
    return pages;
  }

  function generateClinicalGuides (topThemes, themeData): JSX.Element[] {
    const pages = [];

    pages.push(
      <MaterialsCover
        key={currentKey}
        primaryBrand={primaryBrand}
        sectionTypeText="Clinician Resources"
        sectionTitleText="Clinical Guides"
        bookmark="Clinician Resources"
        uuid={uuid}
      >
        <Text>Clinical guides with recommendations to address certain vulnerabilities related to the top themes surfaced in this guide.</Text>
      </MaterialsCover>
    );
    currentKey++;
    
    _.map(topThemes, (theme) => {
      // Cover for theme
      const relevantThemeData: ThemeDataType = _.find(themeData, { id: theme.themeId });

      // Clinical guide
      const features: Record<any, TopFeatureType> = theme.features;
      const relevantFeatureLogics = [];
      const descriptions = [];
      const subPages = [];
      _.map(features, (feature) => {
        const featureLogicIds = _.uniq(_.keys(feature.featureLogics));
        _.map(featureLogicIds, (id) => {
          const relevantFeatureLogicData: FeatureLogicDataType = _.find(featureLogicData, { id: parseInt(id) });
          if (relevantFeatureLogicData === undefined) { return; } // TODO: Log this error
          if (_.indexOf(descriptions, relevantFeatureLogicData.description) > -1) { return; } // TODO: Assess feature logic, VOCs come up frequently with different featureLogicIds
          descriptions.push(relevantFeatureLogicData.description);
          relevantFeatureLogics.push(relevantFeatureLogicData);
          if (!DEBUG) {
            const guidePageNumber = relevantFeatureLogicData.clinical_guide_pdf_page;
            // FIXME: Check if id has two pages in clinical guide
            subPages.push(<ClinicalGuide key={currentKey} pageNumber={guidePageNumber}/>);
          }
          currentKey++;
        });
      });

      pages.push(
        <GuideCover
          primaryBrand={primaryBrand}
          key={currentKey++}
          themeData={relevantThemeData}
          relevantFeatureLogics={relevantFeatureLogics}
          uuid={uuid}
        />,
        ...subPages
      );
      currentKey++;
    });

    return [...pages];
  }

  // FIXME: The child generate functions are very clunky and repeat logic many times
  function generatePages () {
    const pages: JSX.Element[] = [];

    // Create materials section
    // Pull relevant demographic data
    // Top 3 races, all ages (use above median data in future)
    // Generate materials for each demographic
    const flyers: JSX.Element[] = generateFlyers (topThemes, themeData, topRaceEthnicities);
    const linkedIn: JSX.Element[] = generateLinkedIn (topThemes, themeData, topRaceEthnicities);
    const instagram: JSX.Element[] = generateInstagram (topThemes, themeData, topRaceEthnicities);

    // Create guides section
    // Pull relevant featureLogic ids
    // Generate guides for each featureLogic
    const guides: JSX.Element[] = generateClinicalGuides(topThemes, themeData);
    
    pages.push(
      ...flyers,
      ...linkedIn,
      ...instagram,
      ...guides
    );
    return pages;
  }

  return (
    (primaryBrand.id !== -1 && reportData) 
      &&
        <Document
          onRender={
            (blobData) => {
              if (pdfBlob === null) {
                setPdfBlob(blobData, formatPdfFileName(primaryBrand.name, radius, zipCode));
              }
            }
          }>
          <Cover primaryBrand={primaryBrand} reportData={reportData} radius={radius} zipCode={zipCode} uuid={uuid} />
          {/* <IntroPage primaryBrand={primaryBrand} uuid={uuid} /> */}
          <TableOfContents primaryBrand={primaryBrand} uuid={uuid} radius={radius} zipCode={zipCode} totalpopulation_est={reportData.totalpopulation_est} />
          <DataPage primaryBrand={primaryBrand} reportData={reportData} topThemes={topThemes} themeData={themeData} radius={radius} zipCode={zipCode} uuid={uuid} />
          { generatePages() }
          <Cta primaryBrand={primaryBrand} uuid={uuid} />
          <BackCover primaryBrand={primaryBrand} uuid={uuid} />
        </Document>
  );
};

export default RadianPDF;
